import { useEffect, useState, type RefObject } from 'react'

/** True when a sticky element's top has met the scrollport's top. */
export function isStickyStuck(elementTop: number, rootTop: number): boolean {
  return elementTop - rootTop < 1
}

/**
 * Tracks whether `elementRef` is pinned to the top of `scrollRef`.
 * `watch` rebinds after the nodes mount (a view switch, for example).
 */
export function useStickyStuck(
  elementRef: RefObject<HTMLElement | null>,
  scrollRef: RefObject<HTMLElement | null>,
  watch?: unknown,
): boolean {
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const root = scrollRef.current
    const bar = elementRef.current
    if (!root || !bar) {
      setStuck(false)
      return
    }
    const update = () => {
      const next = isStickyStuck(
        bar.getBoundingClientRect().top,
        root.getBoundingClientRect().top,
      )
      setStuck((prev) => (prev === next ? prev : next))
    }
    update()
    root.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(root)
    return () => {
      root.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [elementRef, scrollRef, watch])

  return stuck
}
